import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { Invitation } from '~/api/invitations/types';

import { createAppColumnHelper } from '~/components';
import { ColumnIds } from '~/constants/table';
import { desktopTruncateStyle } from '~/styles/utilities.css';

import { InvitationStatusTag } from './InvitationStatusTag';
import { mobileCellStyle, mobileHeaderStyle } from './SentInvitationsTable.css';

const columnHelper = createAppColumnHelper<Invitation>();

export const useSentInvitationsTableColumns = () => {
  const { t } = useTranslation(['invitations']);

  return useMemo(
    () =>
      columnHelper.columns([
        columnHelper.accessor('name', {
          id: ColumnIds.invitationsName,
          header: t('invitations:sentInvitations.headers.name'),
          cell: ({ getValue }) => <span className={desktopTruncateStyle}>{getValue()}</span>,
          meta: {
            styles: { size: '1fr', minSize: 160 },
            isResizable: true,
          },
        }),

        columnHelper.accessor('email', {
          id: ColumnIds.invitationsEmail,
          header: t('invitations:sentInvitations.headers.email'),
          cell: ({ getValue }) => <span className={desktopTruncateStyle}>{getValue()}</span>,
          meta: {
            styles: { size: '1fr', minSize: 160 },
            isResizable: true,
          },
        }),

        columnHelper.accessor('status', {
          id: ColumnIds.invitationsStatus,
          header: t('invitations:sentInvitations.headers.status'),
          cell: ({ getValue }) => <InvitationStatusTag status={getValue()} />,
          meta: {
            styles: { size: '1fr', minSize: 140 },
          },
        }),

        columnHelper.display({
          id: ColumnIds.invitationsMobile,
          cell: ({ row: { original: invitation } }) => (
            <div className={mobileCellStyle}>
              <div className={mobileHeaderStyle}>
                <span className={desktopTruncateStyle}>{invitation.name}</span>

                <InvitationStatusTag status={invitation.status} />
              </div>

              <span className={desktopTruncateStyle}>{invitation.email}</span>
            </div>
          ),
          meta: { mobile: true },
        }),
      ]),
    [t],
  );
};
